import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import LooksThreeIcon from "@mui/icons-material/Looks3";
import { Alert, Box, Divider, FormControl, Stack, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React from "react";
import { isAwardedBid, isClosedBid, isDeletedBid, isOpenBid, isPendingBid } from "../../util/bid/bidCommon";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import {
  SELECT_HOURS_LIST,
  SELECT_MINUTES_LIST,
  SELECT_IAM_FTO_BID_EFFECTIVE_MONTH_LIST,
  FREEFORM_REASON_CHAR_LIMIT_FOR_BID,
} from "../../util/constants";
import {
  convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight,
  convert_Luxon_To_DateFns,
} from "../../util/dateTimeFormatter";
import StarsSelectInput from "../fundamentals/StarsSelectInput";
import { getTimeZoneFromSwaStation } from "../../util/timeZoneService";
import { set_time_to_midnight } from "../../util/dateTimeFormatter";
import { getDefaultCalendarMonth } from "../../util/bid/IAMFTOBidUtils";
import { getFirstDayWithMonthOffset, getLastDayWithMonthOffset } from "../../util/bid/timeOffBidUtils";
import { SHIFT_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE } from "../../util/messageConstants";

const useStyles = makeStyles((theme) => ({
  doubleDateInput: {
    "& .Mui-selected": {
      color: theme.palette.secondary.swaLightBlue,
    },
  },
  selectInput: {
    width: "180px",
    textAlign: "left",
  },
}));

const SetUpIAMFTOBid = (props) => {
  const classes = useStyles();

  const getBidEffectiveYearAllOptions = () => {
    let bidEffectiveYearAllOptions = [];
    let currYear = DateTime.now().setZone(getTimeZoneFromSwaStation(props.bid.station)).year;
    bidEffectiveYearAllOptions.push(currYear.toString());
    bidEffectiveYearAllOptions.push((currYear + 1).toString());
    return bidEffectiveYearAllOptions;
  };

  const isBidEffectiveMonthDisabled = (monthOptionString) => {
    const currYear = DateTime.now().setZone(getTimeZoneFromSwaStation(props.bid.station)).year;
    if (currYear < props.schedYear) {
      // enable all months if selected year is the next year
      return false;
    }

    const currMonth = DateTime.now().setZone(getTimeZoneFromSwaStation(props.bid.station)).month;
    const monthOptionNumber = DateTime.fromFormat(monthOptionString, "LLLL").month;
    return monthOptionNumber <= currMonth;
  };

  return (
    <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
      <Grid item xs={12} sx={{ padding: "10px" }}>
        <Typography variant="h6" sx={{ textAlign: "left", margin: "10px", fontWeight: "bold" }}>
          {props.bid.station} | {props.bid.workgroup}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="left">
          <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
            <LooksOneIcon color="primary" fontSize="large" />
          </Box>
          <Stack direction="column">
            <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
              <Typography variant="h5">Bid Effective Month</Typography>
              <Typography variant="text">Select the bid effective month for this FTO bid.</Typography>
            </Box>
            <Stack direction="row">
              <Box m={1} sx={{ marginBottom: "20px", maxWidth: "180px" }}>
                {isPendingBid(props.bid.status) ? (
                  <StarsSelectInput
                    label="Bid Effective Year"
                    value={props.schedYear}
                    allOptions={getBidEffectiveYearAllOptions()}
                    onChange={(event) => props.setSchedYear(event)}
                    errors={props.effectiveDatesErrors}
                    className={classes.selectInput}
                  />
                ) : (
                  <FormControl variant="outlined">
                    <TextField
                      className={classes.selectInput}
                      label="Bid Effective Year"
                      variant="outlined"
                      value={props.schedYear}
                      disabled
                    />
                  </FormControl>
                )}
              </Box>
              <Box m={1} sx={{ marginBottom: "20px" }}>
                <StarsSelectInput
                  label="Bid Effective Month"
                  value={props.schedMonth}
                  allOptions={SELECT_IAM_FTO_BID_EFFECTIVE_MONTH_LIST}
                  onChange={(event) => props.setSchedMonth(event)}
                  errors={props.effectiveDatesErrors !== "" ? " " : ""}
                  className={classes.selectInput}
                  isMenuItemDisabled={isBidEffectiveMonthDisabled}
                  disabled={!isPendingBid(props.bid.status) || !props.schedYear}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Stack direction="row" justifyContent="left">
          <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
            <LooksTwoIcon color="primary" fontSize="large" />
          </Box>
          <Stack direction="column">
            <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
              <Typography variant="h5">Bid Window</Typography>
              <Typography variant="text">
                Select which day and time this bid will open. Note that the FTO Bid cannot open until the Shift Bid is
                awarded.
              </Typography>
              <Box mt={1}>
                <Alert severity="warning">{SHIFT_BID_MUST_PUBLISH_BEFORE_WARNING_MESSAGE}</Alert>
              </Box>
            </Box>
            <Stack direction="row">
              <Box m={1} sx={{ marginBottom: "20px" }}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  localeText={{ start: "Bid Open Date", end: "Bid Close Date" }}
                >
                  <MobileDateRangePicker
                    value={[
                      convert_Luxon_To_DateFns(props.windowDates[0]),
                      convert_Luxon_To_DateFns(props.windowDates[1]),
                    ]}
                    onChange={(newValue) => {
                      props.setWindowDates([
                        convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight(newValue[0], props.bid.station),
                        convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight(newValue[1], props.bid.station),
                      ]);
                    }}
                    renderInput={(startProps, endProps) => (
                      <React.Fragment>
                        <TextField
                          {...startProps}
                          error={props.windowDatesErrors !== ""}
                          helperText={props.windowDatesErrors}
                          inputProps={{
                            ...startProps.inputProps,
                            placeholder: "",
                            disabled: startProps.disabled || isOpenBid(props.bid.status),
                          }}
                          disabled={startProps.disabled || isOpenBid(props.bid.status)}
                          sx={{ maxWidth: "250px" }}
                        />
                        <Box sx={{ mx: 2 }}> to </Box>
                        <TextField
                          {...endProps}
                          error={props.windowDatesErrors !== ""}
                          inputProps={{ ...endProps.inputProps, placeholder: "" }}
                        />
                      </React.Fragment>
                    )}
                    className={classes.doubleDateInput}
                    showToolbar={true}
                    disabled={
                      props.schedMonth === "" ||
                      props.schedYear === "" ||
                      isDeletedBid(props.bid.status) ||
                      isClosedBid(props.bid.status) ||
                      isAwardedBid(props.bid.status)
                    }
                    showDaysOutsideCurrentMonth={true}
                    inputFormat="iii MMM dd, yyyy"
                    disableMaskedInput={true}
                    closeOnSelect={true}
                    minDate={
                      props.schedMonth &&
                      props.schedYear &&
                      convert_Luxon_To_DateFns(
                        DateTime.max(
                          set_time_to_midnight(DateTime.now().setZone(getTimeZoneFromSwaStation(props.bid.station))),
                          getFirstDayWithMonthOffset(
                            props.schedMonth,
                            props.schedYear,
                            process.env.REACT_APP_IAM_FTO_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
                          )
                        )
                      )
                    }
                    maxDate={
                      props.schedMonth &&
                      props.schedYear &&
                      convert_Luxon_To_DateFns(getLastDayWithMonthOffset(props.schedMonth, props.schedYear, 1))
                    }
                    defaultCalendarMonth={getDefaultCalendarMonth(props.bid.station, props.schedMonth, props.schedYear)}
                    shouldDisableDate={(_, position) => isOpenBid(props.bid.status) && position === "start"}
                  />
                </LocalizationProvider>
              </Box>
              <Box m={1} sx={{ maxWidth: "180px" }}>
                <StarsSelectInput
                  label="Start/End Time Hr"
                  menuMessage="Select Start/End Time Hr"
                  value={props.windowDateHr}
                  allOptions={SELECT_HOURS_LIST}
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => props.setWindowDateHr(event)}
                  errors={props.windowDateTimeErrors}
                  className={classes.selectInput}
                  disabled={
                    props.windowDates[0] === null || props.windowDates[1] === null || !isPendingBid(props.bid.status)
                  }
                />
              </Box>
              <Box m={1}>
                <StarsSelectInput
                  label="Start/End Time Min"
                  menuMessage="Select Start/End Time Min"
                  value={props.windowDateMin}
                  allOptions={SELECT_MINUTES_LIST}
                  sortComparator={sortOnCaseSensitiveTexts}
                  onChange={(event) => props.setWindowDateMin(event)}
                  errors={props.windowDateTimeErrors !== "" ? " " : ""}
                  className={classes.selectInput}
                  disabled={
                    props.windowDates[0] === null || props.windowDates[1] === null || !isPendingBid(props.bid.status)
                  }
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Stack direction="row" justifyContent="left">
          <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
            <LooksThreeIcon color="primary" fontSize="large" />
          </Box>
          <Stack direction="column">
            <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
              <Typography variant="h5">Bid Name</Typography>
              <Typography variant="text">You can enter a custom name for this FTO bid. It is not required.</Typography>
            </Box>
            <Box m={1} sx={{ textAlign: "left", marginBottom: "20px" }}>
              <TextField
                sx={{ width: "500px" }}
                label="Bid Name"
                variant="outlined"
                value={props.bidName}
                onChange={props.setBidNameChange}
                disabled={
                  isDeletedBid(props.bid.status) ||
                  isOpenBid(props.bid.status) ||
                  isClosedBid(props.bid.status) ||
                  isAwardedBid(props.bid.status)
                }
                error={props.bidNameErrors !== ""}
                helperText={props.bidNameErrors}
              />
              {props.bidName.length > FREEFORM_REASON_CHAR_LIMIT_FOR_BID ? (
                <Box sx={{ color: "primary.swaRed", textAlign: "right" }}>
                  {props.bidName.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
                </Box>
              ) : (
                <Box sx={{ textAlign: "right" }}>
                  {props.bidName.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};

SetUpIAMFTOBid.propTypes = {
  bid: PropTypes.object.isRequired,
  schedMonth: PropTypes.string.isRequired,
  setSchedMonth: PropTypes.func.isRequired,
  schedYear: PropTypes.number.isRequired,
  setSchedYear: PropTypes.func.isRequired,
  effectiveDatesErrors: PropTypes.string.isRequired,
  windowDates: PropTypes.array.isRequired,
  setWindowDates: PropTypes.func.isRequired,
  windowDatesErrors: PropTypes.string.isRequired,
  windowDateHr: PropTypes.string.isRequired,
  setWindowDateHr: PropTypes.func.isRequired,
  windowDateMin: PropTypes.string.isRequired,
  setWindowDateMin: PropTypes.func.isRequired,
  windowDateTimeErrors: PropTypes.string.isRequired,
  bidName: PropTypes.string.isRequired,
  bidNameErrors: PropTypes.string.isRequired,
  setBidNameChange: PropTypes.func.isRequired,
};

export default SetUpIAMFTOBid;
